import { generateEntityCode } from '@erp/domain/numbering';
import type { TypedSupabaseClient } from '../client';

// Scope → nom de la colonne compteur + préfixe/padding à lire dans company_settings.
// Aligné avec la migration 20260421000000_auto_increment_codes.sql.
type CodeScope = 'client' | 'supplier' | 'article' | 'barcode';

const SCOPE_META: Record<CodeScope, {
  counter: 'client_code_next' | 'supplier_code_next' | 'article_code_next' | 'barcode_next';
  prefixCol: string;
  paddingCol: string;
  defaultPrefix: string;
  defaultPadding: number;
}> = {
  client:   { counter: 'client_code_next',   prefixCol: 'client_code_prefix',   paddingCol: 'client_code_padding',   defaultPrefix: 'CLI', defaultPadding: 4 },
  supplier: { counter: 'supplier_code_next', prefixCol: 'supplier_code_prefix', paddingCol: 'supplier_code_padding', defaultPrefix: 'FRN', defaultPadding: 4 },
  article:  { counter: 'article_code_next',  prefixCol: 'article_code_prefix',  paddingCol: 'article_code_padding',  defaultPrefix: 'ART', defaultPadding: 4 },
  barcode:  { counter: 'barcode_next',       prefixCol: 'barcode_prefix',      paddingCol: 'barcode_padding',      defaultPrefix: '',    defaultPadding: 8 },
};

async function readSettingsForScope(
  supabase: TypedSupabaseClient,
  scope: CodeScope,
): Promise<{ prefix: string; padding: number; next: number }> {
  const meta = SCOPE_META[scope];
  // Admin users may see >1 tenant; filter by current_user_tenant_id()
  // so the prefix/counter we read matches the tenant the BEFORE-INSERT
  // trigger will write into. Picking by id-asc would risk reading a
  // different tenant's prefix and storing duplicate codes.
  type SbAny = { rpc: (fn: string, args: Record<string, unknown>) => Promise<{ data: string | null; error: { message: string } | null }> };
  const { data: tenantId } = await (supabase as unknown as SbAny).rpc(
    'current_user_tenant_id',
    {},
  );
  const { data } = await supabase
    .from('company_settings')
    .select('*')
    .eq('tenant_id', tenantId ?? '')
    .maybeSingle();
  const row = (data ?? {}) as Record<string, unknown>;
  return {
    prefix: (row[meta.prefixCol] as string | null) ?? meta.defaultPrefix,
    padding: Number(row[meta.paddingCol] ?? meta.defaultPadding),
    next: Number(row[meta.counter] ?? 1),
  };
}

/** Retourne le prochain code pour affichage, sans incrémenter. */
export async function peekEntityCode(
  supabase: TypedSupabaseClient,
  scope: CodeScope,
): Promise<string> {
  const { prefix, padding, next } = await readSettingsForScope(supabase, scope);
  return generateEntityCode(prefix, next, padding);
}

/**
 * Consomme atomiquement le compteur (via RPC) et retourne le code formaté.
 * À n'appeler que pendant la création effective de l'entité.
 */
export async function consumeEntityCode(
  supabase: TypedSupabaseClient,
  scope: CodeScope,
): Promise<string> {
  const meta = SCOPE_META[scope];
  // RPC ajoutée dans la migration — pas encore dans database.types générées.
  // Appel direct sur `supabase` pour préserver le binding `this`
  // (supabase-js lit `this.rest` en interne — une ref déliée plante).
  const { data, error } = await (supabase.rpc as unknown as (
    fn: string,
    args: Record<string, unknown>,
  ) => Promise<{ data: number | null; error: { message: string } | null }>)
    .call(supabase, 'consume_entity_code', { counter_name: meta.counter });

  // Lit le préfixe/padding en parallèle du compteur (les settings ne changent presque jamais).
  const { prefix, padding, next } = await readSettingsForScope(supabase, scope);

  if (error || data == null) {
    // Fallback : utilise la valeur courante (moins sûr, mais conforme au contrat).
    return generateEntityCode(prefix, next, padding);
  }
  return generateEntityCode(prefix, Number(data), padding);
}
